// ─── /privacy and /terms ─────────────────────────────────────────────────────
// Public, no sign-in. Both cover Messages (Facebook and Instagram) only for
// now. /privacy is titled "Privacy Policy" with Messages as its first section,
// so other sections can be added later without changing the URL. Meta's app
// settings point at both URLs, so they are prerendered (scripts/prerender.mjs)
// and readable without JavaScript.
//
// Wording approved by Josh, 2026-10-23. Change it only with his approval, and
// move EFFECTIVE when the published text changes.

import React from 'react'
import { FONT_HEAD, FONT_BODY } from './styles'

// The day the current text was published.
const EFFECTIVE = 'October 23, 2026'

const NAVY = '#1d2e42'
const GOLD = '#fdbf00'
const BLUE = '#147efb'
const PAGE = '#f5f5f5'
const HAIRLINE = '#e8e8e8'
const BODY_TEXT = '#333'
const MUTED = '#666'

const OPERATOR = ['HomePros', '9059 W McDowell Rd, Suite 200, Tolleson, AZ 85353']
const EMAIL = 'broker@homeprosarizona.com'

const css = `
.legal-wrap { max-width: 780px; margin: 0 auto; padding: 0 24px; box-sizing: border-box; }
.legal-card { background: #ffffff; border: 0.5px solid ${HAIRLINE}; border-radius: 14px; padding: 36px 40px; }
.legal-card p, .legal-card li { font-size: 15px; line-height: 1.7; color: ${BODY_TEXT}; }
.legal-card p { margin: 0 0 12px; }
.legal-card ul, .legal-card ol { margin: 0 0 14px; padding-left: 22px; }
.legal-card li { margin-bottom: 6px; }
.legal-card a { color: ${BLUE}; }
.legal-nav a { color: ${MUTED}; text-decoration: none; }
.legal-nav a[aria-current="page"] { color: ${NAVY}; font-weight: 700; }
@media (max-width: 640px) {
  .legal-wrap { padding: 0 16px; }
  .legal-card { padding: 22px 18px; }
  .legal-card p, .legal-card li { font-size: 14.5px; }
}
`

const Mail = () => <a href={`mailto:${EMAIL}`}>{EMAIL}</a>

function H2({ children }) {
  return (
    <h2 style={{fontFamily:FONT_HEAD,fontWeight:900,fontSize:'22px',letterSpacing:'0.5px',color:NAVY,margin:'0 0 6px',textTransform:'uppercase'}}>
      {children}
    </h2>
  )
}

function H3({ children }) {
  return (
    <h3 style={{fontFamily:FONT_HEAD,fontWeight:700,fontSize:'15px',letterSpacing:'1.5px',color:NAVY,margin:'26px 0 8px',textTransform:'uppercase'}}>
      {children}
    </h3>
  )
}

function H4({ children }) {
  return <p style={{fontWeight:700,color:NAVY,margin:'14px 0 6px'}}>{children}</p>
}

function Contact() {
  return (
    <p>
      {OPERATOR[0]}, {OPERATOR[1]}<br />
      <Mail />
    </p>
  )
}

function LegalShell({ current, title, children }) {
  React.useEffect(() => { document.title = `${title} | TrueTopProducer` }, [title])
  const link = (href, label, key) => (
    <a href={href} aria-current={current === key ? 'page' : undefined}>{label}</a>
  )
  return (
    <div style={{minHeight:'100vh',background:PAGE,fontFamily:FONT_BODY}}>
      <style>{css}</style>
      <div style={{borderBottom:`0.5px solid ${HAIRLINE}`}}>
        <div className="legal-wrap" style={{display:'flex',alignItems:'center',justifyContent:'space-between',height:'54px',gap:'12px'}}>
          <a href="/" style={{display:'flex',alignItems:'center',gap:'9px',textDecoration:'none',minWidth:0}}>
            <span style={{color:GOLD,fontSize:'16px',lineHeight:1}}>&#9654;</span>
            <span style={{fontSize:'14px',fontWeight:900,letterSpacing:'1.5px',color:NAVY,fontFamily:FONT_HEAD,whiteSpace:'nowrap'}}>TrueTopProducer</span>
          </a>
          <nav className="legal-nav" style={{display:'flex',gap:'18px',fontSize:'12.5px',whiteSpace:'nowrap'}}>
            {link('/privacy', 'Privacy', 'privacy')}
            {link('/terms', 'Terms', 'terms')}
          </nav>
        </div>
      </div>

      <div className="legal-wrap" style={{paddingTop:'32px',paddingBottom:'48px'}}>
        <h1 style={{fontFamily:FONT_HEAD,fontWeight:900,fontSize:'38px',lineHeight:1.05,color:NAVY,margin:'0 0 6px',textTransform:'uppercase',letterSpacing:'0.5px'}}>{title}</h1>
        <div style={{fontSize:'13px',color:MUTED,marginBottom:'22px'}}>Effective {EFFECTIVE}</div>
        <div className="legal-card">{children}</div>

        <div className="legal-nav" style={{marginTop:'22px',fontSize:'12.5px',color:MUTED,lineHeight:1.7,display:'flex',gap:'8px 18px',flexWrap:'wrap'}}>
          {link('/privacy', 'Privacy Policy', 'privacy')}
          {link('/terms', 'Terms of Use', 'terms')}
          <span>{OPERATOR[0]} · {OPERATOR[1]}</span>
        </div>
      </div>
    </div>
  )
}

export function PrivacyPage() {
  return (
    <LegalShell current="privacy" title="Privacy Policy">
      <H2>Messages (Facebook and Instagram)</H2>
      <p>This section explains what happens when a real estate agent connects a Facebook Page and its Instagram account to TrueTopProducer's Messages feature, and what happens to the messages people send to that Page or Instagram account.</p>

      <H3>Who runs this</H3>
      <p>TrueTopProducer is operated by HomePros (designated broker: Josh Marquez).</p>
      <Contact />

      <H3>What we receive when you connect</H3>
      <p>When you connect, Facebook asks you to approve access. If you approve, we receive and keep:</p>
      <ul>
        <li>An access token for the Page you choose. We store it encrypted and use it only for the purposes below.</li>
        <li>The Page's id and name, and the id and username of the Instagram account linked to it.</li>
        <li>Your Facebook user id, only so we can find and remove your connection if you later remove the app in Facebook or ask Facebook to delete your data.</li>
        <li>Your own Follow Up Boss user id, so new contacts are assigned to you.</li>
      </ul>
      <p>Facebook may also show a business management permission; we use it only so Facebook Login for Business can list the Pages you manage.</p>

      <H3>What we receive after you connect</H3>
      <p>From then on, Meta sends us the direct messages (DMs) on that Page and Instagram account: messages people send you, and replies you send. For each conversation we keep:</p>
      <ul>
        <li>The other person's Messenger or Instagram id, and their name or username.</li>
        <li>The text of each message, whether it was sent or received, and when.</li>
        <li>The Follow Up Boss contact id we created for that conversation.</li>
      </ul>
      <p>Photos, videos and other attachments are not stored. We keep only a label such as "[image attachment]".</p>

      <H3>What we use it for</H3>
      <p>Only three things:</p>
      <ol>
        <li>To show your DMs in Messages on TrueTopProducer.</li>
        <li>To log each conversation in your own Follow Up Boss account: a new contact assigned to you, and each message as a note.</li>
        <li>To send the replies you write in Messages, from your Page or Instagram account.</li>
      </ol>
      <p>We also show you an in-app notice when a new message arrives.</p>

      <H3>What we do not do</H3>
      <ul>
        <li>We do not post anything to your Page, your Instagram account or anywhere else.</li>
        <li>Besides DMs, we read only the list of Pages you manage, their names, and the username of the Instagram account linked to each. We do not read posts, comments, ads, insights or followers.</li>
        <li>We do not access your personal Facebook profile, your friends or your personal messages.</li>
        <li>We do not send any message you did not write.</li>
        <li>We do not sell, rent or share this data with anyone, and we do not use it for advertising. The only place it goes is your own Follow Up Boss account, and that is because you connected it.</li>
      </ul>

      <H3>Meta's 24-hour reply rule</H3>
      <p>Meta lets a business reply to a person by Messenger or Instagram DM only within 24 hours of that person's most recent message. After 24 hours, Messages won't let you send a reply. You can reply again once the person messages you. This is Meta's rule, not ours.</p>

      <H3>Where it is stored</H3>
      <p>Your data is stored in TrueTopProducer's database, hosted by our infrastructure providers, who only provide the service. Page tokens are encrypted. Only you can see your conversations in Messages. HomePros staff access the data only to run, secure or fix the service.</p>

      <H3>How long we keep it</H3>
      <p>We keep your conversations for as long as your Page is connected. They are deleted as described below.</p>

      <H3>Disconnecting and deletion</H3>
      <H4>Disconnect in Messages</H4>
      <ul>
        <li>Meta stops sending us that Page's DMs.</li>
        <li>The Page connection and its encrypted token are deleted, along with every conversation and message from that Page stored in TrueTopProducer.</li>
        <li>If it was the last Page you connected with that Facebook login, we also remove the app's permissions from your Facebook account.</li>
      </ul>
      <H4>Removing the app in Facebook (Settings &gt; Business Integrations, or Apps and Websites)</H4>
      <ul>
        <li>Facebook tells us automatically. We delete every Page connection you made with that Facebook login, with their tokens, conversations and messages.</li>
      </ul>
      <H4>Data deletion request through Facebook</H4>
      <ul>
        <li>We delete everything we hold from that Facebook login (connections, tokens, conversations and messages) right away. Facebook gives you a confirmation code and a link where you can check that the deletion is complete.</li>
      </ul>
      <H4>If you leave HomePros or HomePros Advantage</H4>
      <ul>
        <li>If you leave HomePros or HomePros Advantage, new messages stop immediately, and we delete your Messages data within 30 days or sooner on request.</li>
      </ul>
      <H4>What stays in Follow Up Boss</H4>
      <ul>
        <li>Contacts and notes already written to your Follow Up Boss account are NOT deleted by any of the steps above. Follow Up Boss is your own CRM, and those records are yours. You can delete them yourself in Follow Up Boss.</li>
      </ul>
      <p>You can also email <Mail /> to ask us to delete your data.</p>

      <H3>If you messaged an agent's Page</H3>
      <p>If you sent a DM to a Facebook Page or Instagram account connected to TrueTopProducer, your name or username, your Messenger or Instagram id, and your messages are handled as described above so the agent can read and reply to them and keep a record in their own CRM. Your data is removed from TrueTopProducer when the agent disconnects. To ask about your data, email <Mail />.</p>

      <H3>Changes</H3>
      <p>If we change this policy, we will update it on this page and change the effective date.</p>

      <H3>Contact</H3>
      <Contact />
    </LegalShell>
  )
}

export function TermsPage() {
  return (
    <LegalShell current="terms" title="Terms of Use">
      <H2>Messages (Facebook and Instagram)</H2>

      <H3>Who can use it</H3>
      <p>Messages is available only to licensed agents of HomePros and HomePros Advantage with an active TrueTopProducer account. If you leave the brokerage, Messages stops working for your connected Page, and you should disconnect it.</p>

      <H3>What you need</H3>
      <p>You must be an admin of the Facebook Page you connect, and connecting its Instagram account requires that account to be a professional account linked to that Page. Logging to Follow Up Boss requires your own Follow Up Boss API key in TrueTopProducer.</p>

      <H3>Your messages are your responsibility</H3>
      <p>You write every reply sent through Messages, and you are responsible for it. That includes following:</p>
      <ul>
        <li>Arizona Department of Real Estate (ADRE) rules on advertising and communications, including disclosing your brokerage as those rules require;</li>
        <li>Fair housing laws;</li>
        <li>Meta's terms and policies for Facebook, Messenger and Instagram.</li>
      </ul>
      <p>HomePros does not review your messages before they are sent.</p>

      <H3>Meta's rules apply</H3>
      <p>Messages works through Meta's tools and depends on them. Meta's limits, such as the 24-hour reply rule, apply. If Meta changes or limits access, parts of Messages may stop working.</p>

      <H3>Follow Up Boss</H3>
      <p>Conversations are logged to your own Follow Up Boss account. Those records are yours. Disconnecting from Messages does not delete them.</p>

      <H3>Changes and ending the service</H3>
      <p>HomePros may change, pause or stop Messages at any time, with or without notice. HomePros may disconnect a Page that breaks these terms.</p>

      <H3>No warranty</H3>
      <p>Messages is provided as is. Delivery depends on Meta and Follow Up Boss, and messages can be delayed or missed. Always check your Page and Instagram directly for anything urgent.</p>

      <H3>Limitation of liability</H3>
      <p>HomePros is not liable for missed, delayed or misdelivered messages, or for any indirect, incidental or consequential damages. HomePros's total liability for Messages is limited to the amount you paid for Messages, which is currently $0.</p>

      <H3>Governing law</H3>
      <p>These terms are governed by the laws of the State of Arizona.</p>

      <H3>Privacy</H3>
      <p>How we handle data is described in our <a href="/privacy">Privacy Policy</a>.</p>

      <H3>Contact</H3>
      <Contact />
    </LegalShell>
  )
}
